import { useEffect } from 'react'

// Components
import { Error, Image } from '@tracklib/kit'
import { StreamField } from 'components/staticpages/streamfield'
import ArticleCategories from 'components/articles/categories'
import CommentView from 'components/comments'
import Hero from 'components/articles/hero'
import Intro from 'components/articles/intro'
import SEO from 'components/base/seo'
import ThemeHelper from 'components/base/themehelper'
import TrialAndNewsletterCta from 'components/base/trialandnewslettercta'

// Utils
import { HOSTNAME, CDN_ROOT } from '@constants'

// Types
import type { CmsPage } from 'types'

import styles from './detail.css'

export const processInstagram = (): void => {
  const elem = document.getElementById('instagram')
  if (!elem) {
    const script = document.createElement('script')
    script.src = 'https://www.instagram.com/embed.js'
    script.async = true
    script.id = 'instagram'
    if (document.head) {
      document.head.appendChild(script)
    }
  }

  if (window.instgrm) {
    window.instgrm.Embeds.process()
  }
}

type ArticleDetailProps = {
  article: CmsPage
}

const ArticleDetail = ({ article }: ArticleDetailProps): JSX.Element | null => {
  const contentLength = article?.content?.length || 0
  useEffect(() => {
    const hasInstagramEmbed = JSON.stringify(article?.content)?.includes('instagram-media')
    if (hasInstagramEmbed) {
      processInstagram()
    }
  }, [contentLength]) // eslint-disable-line

  if (!article) {
    return null
  }

  const noPermission = false
  if (noPermission) {
    return <Error message="You don't have permission to view this article." emoji="sad" />
  }

  const { meta, title, image, sharing_image, hide, summary, content, categories, id, hide_comments, theme, author } = article
  const date = meta.first_published_at

  const getSEOTitle = (): string => {
    const { seo_title } = meta
    if (seo_title) {
      return seo_title
    }
    if (title) {
      const string = title || 'Tracklib blog'
      return `${string.charAt(0).toUpperCase()}${string.slice(1)}`
    }
    return 'Sample on Tracklib'
  }
  return (
    <>
      <SEO
        title={`${getSEOTitle()} | Tracklib Blog`}
        description={summary}
        image={Image.getAbsoluteSrc(HOSTNAME, CDN_ROOT, (sharing_image && sharing_image.id) || (image && image.id), 2400, 1260, 'png')}
        imageWidth={2400}
        imageHeight={1260}
        type="article"
        published={date}
        noIndex={hide}
      >
        <meta name="twitter:label1" content={categories.length ? 'Categories' : 'Written by'} />
        <meta name="twitter:data1" content={categories.length ? categories.map(category => category.name).join(', ') : author.alias} />
        <meta name="twitter:label2" content="Get a free trial" />
        <meta name="twitter:data2" content="https://tracklib.com/subscribe" />
      </SEO>
      <ThemeHelper theme={theme} />
      <article className={styles.article}>
        <Hero article={article} />
        <div className={styles.container} id="articleBody">
          <Intro article={article} />
          <div className={styles.body}>
            <StreamField blocks={content} isArticle />
          </div>
          <div className={styles.maxWidth}>
            <TrialAndNewsletterCta />
            <br />
            <ArticleCategories categories={categories} />
          </div>
          <hr />
          {!hide_comments && <CommentView objectType="articlepage" objectId={id} className={styles.comments} />}
        </div>
      </article>
    </>
  )
}

export default ArticleDetail
